import React, { useState, useEffect } from 'react';
import { Box, Typography, useTheme } from '@mui/material';

/**
 * Get rating text from score.
 */
function getRating(score) {
  if (score >= 90) return 'Excelente';
  if (score >= 75) return 'Muy bueno';
  return 'Bueno';
}

/**
 * FinMaple Score badge - vertical stack, 2-tier color (blue/gray).
 * - 40px ring with 2.5px border
 * - Numeric centered in ring
 * - Rating label centered below (0.6rem)
 * - "¿Cómo calculamos?" link below (0.55rem)
 * - Animated counter 0→score in 400ms ease-out
 */
const UniversalScoreBadge = ({ item, onExplainerClick }) => {
  const theme = useTheme();
  const score = item?.ranking?.finmaple_score ?? 0;
  const [display, setDisplay] = useState(0);

  // Animate from 0 → score on mount
  useEffect(() => {
    if (score === 0) return;
    const duration = 400;
    const start = performance.now();
    let raf;
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(score * eased));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [score]);

  const color =
    score >= 75 ? theme.palette.primary.main :
    theme.palette.text.disabled;

  const rating = getRating(score);

  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
      {/* Ring */}
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          border: `2.5px solid ${color}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.9rem',
            fontWeight: 700,
            fontFeatureSettings: "'tnum'",
            color,
            lineHeight: 1,
          }}
        >
          {display}
        </Typography>
      </Box>

      {/* Rating label */}
      <Typography
        variant="caption"
        sx={{
          color,
          fontWeight: 700,
          lineHeight: 1.2,
          fontSize: '0.75rem',
          textAlign: 'center',
        }}
      >
        {rating}
      </Typography>

      {/* Explainer link */}
      {onExplainerClick && (
        <Typography
          component="button"
          onClick={onExplainerClick}
          sx={{
            background: 'none',
            border: 'none',
            p: 0,
            cursor: 'pointer',
            color: 'text.disabled',
            fontSize: '0.75rem',
            textDecoration: 'underline',
            textDecorationColor: 'divider',
            textAlign: 'center',
            '&:hover': { color: 'primary.main' },
          }}
        >
          ¿Cómo calculamos?
        </Typography>
      )}
    </Box>
  );
};

export default UniversalScoreBadge;
